import React, { useState, useMemo, useCallback } from 'react';
import { createColumnHelper } from '@tanstack/react-table';

/**
 * Custom hook for managing common data table operations
 * @param {Object} params - Configuration parameters
 * @param {Array} params.data - The data array
 * @param {Function} params.onDataChange - Callback to update data
 * @param {Array} params.fields - Field definitions for column generation
 * @param {Array} params.columns - Optional pre-defined columns
 * @param {Object} params.optionsMap - Map of options for auto fields
 * @param {Function} params.setHasUnsaved - Callback to set unsaved changes flag
 * @returns {Object} - Hook state and handlers
 */
export const useDataTable = ({
  data = [],
  onDataChange,
  fields = [],
  columns,
  optionsMap = {},
  setHasUnsaved,
  useESLineEdit,
  useHtsEdit,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isESLineModalOpen, setIsESLineModalOpen] = useState(false);
  const [isHtsModalOpen, setIsHtsModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [openHtsLookupDialog, setOpenHtsLookupDialog] = useState(false);

  // Helper function to format field values based on type
  const formatFieldValue = useCallback((value, field) => {
    if (value === '' || value === null || value === undefined) return '-';

    // Handle array values
    if (Array.isArray(value)) {
      return value.length > 0 ? value.map((item, index) => `${item}`).join(',\n') : '-';
    }

    // Handle boolean fields (Toggle, Switch, Checkbox)
    if (field.type === 'toggle' || field.type === 'switch' || field.type === 'checkbox') {
      return value === true ? 'Yes' : 'No';
    }

    // Handle auto field types            
    if (field.type?.startsWith("auto")) {

      // Look up the option in optionsMap
      const options = optionsMap[field.name];
      if (!options || !Array.isArray(options)) return String(value);

      const option = options.find(opt => opt.value === value);
      if (!option) return String(value);

      // Format based on field type
      if (field.type === 'auto description') {
        return option.description;
      } else if (field.type === 'auto value') {
        return option.value;
      } else {
        // Default "auto" type - show "value - description"
        return `${option.value} - ${option.description}`;
      }
    }

    // Default for other field types
    return String(value);
  }, [optionsMap]);

  // Generate columns dynamically from fields if no columns prop provided
  const generatedColumns = useMemo(() => {
    if (columns) return columns;

    const columnHelper = createColumnHelper();
    return fields.filter(f => !f.ignoreColumn).map(field =>
      columnHelper.accessor(field.name, {
        header: field.label,
        cell: (info) => {
          const value = info.getValue();
          if (Array.isArray(value)) {
            return <div style={{ whiteSpace: 'pre-line' }}>{formatFieldValue(value, field)}</div>;
          }
          return formatFieldValue(value, field);
        },
      })
    );
  }, [columns, fields, formatFieldValue]);

  const handleAddRow = () => {
    setEditingRow(null);
    setEditingIndex(null);
    if(useESLineEdit) {
      setIsESLineModalOpen(true);
      setIsModalOpen(false);
      setIsHtsModalOpen(false)
    } else if(useHtsEdit) {
      setIsHtsModalOpen(true)
      setIsESLineModalOpen(false);
      setIsModalOpen(false);
    } else {
      setIsModalOpen(true);
      setIsESLineModalOpen(false);
      setIsHtsModalOpen(false)
    }
  };

  const handleEditRow = (row, index) => {
    setEditingRow(row);
    setEditingIndex(index);

    if(useESLineEdit) {
      setIsESLineModalOpen(true);
      setIsModalOpen(false);
      setIsHtsModalOpen(false)
    } else if(useHtsEdit) {
      setIsHtsModalOpen(true)
      setIsESLineModalOpen(false);
      setIsModalOpen(false);
    } else {
      setIsModalOpen(true);
      setIsESLineModalOpen(false);
      setIsHtsModalOpen(false)
    }
  };

  const handleSave = (rowData, rhfArray) => {
    if (!!rhfArray) {
      const updateIndex = editingIndex !== null
        ? editingIndex : rhfArray.fields.length

      rhfArray.update(updateIndex, rowData)
    } else {
      let newData
      if (editingIndex !== null) {
        // Update existing row
        newData = [...data];
        newData[editingIndex] = rowData;
      } else
        newData = [...data, rowData]
      onDataChange(newData)
    }

    // Set unsaved changes flag
    if (setHasUnsaved) {
      setHasUnsaved(true);
    }

    if(useESLineEdit)
      setIsESLineModalOpen(false)
    else
      setIsModalOpen(false);
  };

  const handleDelete = () => {
    if (editingIndex !== null) {
      const newData = data.filter((_, index) => index !== editingIndex);
      onDataChange(newData);

      // Set unsaved changes flag
      if (setHasUnsaved) {
        setHasUnsaved(true);
      }
    }
    if(useESLineEdit)
      setIsESLineModalOpen(false)
    else if(useHtsEdit)
      setIsHtsModalOpen(false)
    else
      setIsModalOpen(false);
  };

  const handleCloseModal = () => {
    if(useESLineEdit)
      setIsESLineModalOpen(false)
    else if(useHtsEdit)
      setIsHtsModalOpen(false)
    else
      setIsModalOpen(false);
  };

  return {
    // State
    isModalOpen,
    isESLineModalOpen,
    isHtsModalOpen,
    editingRow,
    editingIndex,
    generatedColumns,
    openHtsLookupDialog,
    setOpenHtsLookupDialog,

    // Handlers
    handleAddRow,
    handleEditRow,
    handleSave,
    handleDelete,
    handleCloseModal,

    // Utility functions
    formatFieldValue,
  };
};
